import React, { Fragment, useState } from 'react';
import { usePopper } from 'react-popper';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { NavLink } from 'react-router-dom';
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
} from '@headlessui/react';
import { search } from 'flow/actions/app-actions';
import SearchBar from 'flow/components/SearchBar';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { routes } from 'flow/types/routes';
import ChatSessionsList from './ChatSessionsList';
/* The chat list is virtualised, so it renders the same handful of rows however
 * many chats match. That leaves the wait free to sit under a typing interval,
 * where results track the field instead of arriving after it. */
const CHAT_SEARCH_DEBOUNCE_MS = 300;

const Sessions = () => {
    const [extensionFilter, setExtensionFilter] = useState('');

    const dispatch = useAppDispatch();

    /* SearchBar hands the text over already debounced, so this dispatches as
     * it arrives - waiting again only adds to the delay before the list
     * catches up with the field. */
    const handleFilter = (searchText: string) => {
        setExtensionFilter(searchText);
        dispatch(search(searchText));
    };

    return (
        <div className="flex h-full flex-1 flex-col border-e bg-gray-100">
            <div className="p-3">
                <SearchBar
                    onChange={handleFilter}
                    initialSearchText={extensionFilter}
                    debounceMs={CHAT_SEARCH_DEBOUNCE_MS}
                >
                    <>
                        <NewChatButton />
                        <ActionButton />
                    </>
                </SearchBar>
            </div>
            <ChatSessionsList />
        </div>
    );
};

const NewChatButton = () => {
    const { i18next } = useAppContext();

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'flip',
                enabled: true,
                options: {
                    flipVariations: true,
                    allowedAutoPlacements: ['bottom-start', 'bottom-end'],
                },
            },
            { name: 'preventOverflow', enabled: true },
        ],
    });

    return (
        <Menu as="div">
            {({ open }) => (
                <div className="relative">
                    <MenuButton
                        ref={setReferenceElement}
                        className="size-10 items-center rounded-sm bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-700"
                    >
                        <span className="sr-only">
                            {i18next.t('chat.new_chat')}
                        </span>
                        <Icon iconKey="add" />
                    </MenuButton>

                    <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            ref={setPopperElement}
                            style={styles.popper}
                            {...attributes.popper}
                            className="shadow-base start-0 top-11 z-30 mt-1 flex w-max flex-col gap-4 rounded-sm bg-white p-3 text-gray-800"
                        >
                            <MenuItems
                                as="div"
                                className="flex max-h-[520px] origin-bottom-right flex-col gap-2 overflow-hidden outline-hidden rtl:origin-top-left"
                            >
                                <MenuItem>
                                    <NavLink to={routes['messages.create']}>
                                        <button className="group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200">
                                            <Icon
                                                iconKey="message"
                                                className="text-gray-600 group-hover:text-gray-700"
                                            />
                                            <p>{i18next.t('chat.new_chat')}</p>
                                        </button>
                                    </NavLink>
                                </MenuItem>
                            </MenuItems>
                        </div>
                    </Transition>
                </div>
            )}
        </Menu>
    );
};

const ActionButton = () => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'flip',
                enabled: true,
                options: {
                    flipVariations: true,
                    allowedAutoPlacements: ['bottom-start', 'bottom-end'],
                },
            },
            { name: 'preventOverflow', enabled: true },
        ],
    });

    return (
        <Menu as="div">
            {({ open }) => (
                <div className="relative">
                    <MenuButton
                        ref={setReferenceElement}
                        className="size-10 items-center rounded-sm bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-700"
                    >
                        <span className="sr-only">
                            {i18next.t('settings.more_options')}
                        </span>
                        <Icon iconKey="more_vert" />
                    </MenuButton>

                    <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            ref={setPopperElement}
                            style={styles.popper}
                            {...attributes.popper}
                            className="shadow-base start-0 top-11 z-30 mt-1 flex w-max flex-col gap-4 rounded-sm bg-white p-2 text-gray-800"
                        >
                            <MenuItems
                                as="div"
                                className="max-h-[520px] origin-bottom-right overflow-hidden outline-hidden rtl:origin-top-left"
                            >
                                <MenuItem key="list-rooms-modal">
                                    <button
                                        className="group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200"
                                        onClick={() => openModal('listRooms')}
                                    >
                                        <Icon
                                            iconKey="public"
                                            className="text-gray-600 group-hover:text-gray-700"
                                        />
                                        <p>
                                            {i18next.t(
                                                'Chat.Create.Alert.Public',
                                            )}
                                        </p>
                                    </button>
                                </MenuItem>

                                <MenuItem key="archived-rooms">
                                    <button
                                        className="group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200"
                                        onClick={() =>
                                            openModal('archivedChats')
                                        }
                                    >
                                        <Icon
                                            iconKey="archive"
                                            className="text-gray-600 group-hover:text-gray-700"
                                        />
                                        <p>
                                            {i18next.t('chat.archived.title')}
                                        </p>
                                    </button>
                                </MenuItem>
                            </MenuItems>
                        </div>
                    </Transition>
                </div>
            )}
        </Menu>
    );
};

export default Sessions;
